import React, { useEffect, useState } from 'react';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { useUserDropdown } from '@/hooks/useUserDropdown';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useSuperadminRole } from '@/hooks/superadmin/useSuperadminRole';
import DropdownHeader from './dropdown/DropdownHeader';
import NavigationItems from './dropdown/NavigationItems';
import LogoutButton from './dropdown/LogoutButton';
import DropdownTrigger from './dropdown/DropdownTrigger';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface UserDropdownProps {
  isLoggingOut: boolean;
  onLogout: () => Promise<void>;
  isAdmin?: boolean;
}

const UserDropdown = ({ isLoggingOut, onLogout, isAdmin: propsIsAdmin }: UserDropdownProps) => {
  const {
    user,
    isAdmin: hookIsAdmin,
    username,
    displayName,
    avatarUrl,
    getInitials,
    isOnDashboard,
    isOnProfilePage,
    isOnHomePage,
    isOnAdminPage,
    isOnSettingsPage,
    isOnNotificationsPage,
    isOnSuperadminPage,
    usernameSlug
  } = useUserDropdown();
  
  const { isAdmin: checkedIsAdmin } = useAdminCheck(user?.id);
  
  const { 
    isSuperadmin, 
    isLoading: isSuperadminLoading,
  } = useSuperadminRole();
  
  const { toast } = useToast();
  const [showDebugMenu, setShowDebugMenu] = useState(false);
  const [verifiedStatus, setVerifiedStatus] = useState(false);
  
  const isAdmin = propsIsAdmin !== undefined ? propsIsAdmin : 
                  hookIsAdmin !== undefined ? hookIsAdmin : 
                  checkedIsAdmin;
  
  // useEffect(() => {
  //   let mounted = true;
    
  //   const autoVerify = async () => {
  //     if (user?.id && !verifiedStatus && !isSuperadmin) {
  //       console.log('[UserDropdown] Auto-verifying superadmin status for:', user.email);
  //       try {
  //         if (user.email === 'guidione.machava@gmail.com') {
  //           console.log('[UserDropdown] Special handling for guidione.machava@gmail.com');
  //           const result = await clearCacheAndRecheck();
  //           if (mounted) {
  //             setVerifiedStatus(true);
  //             console.log('[UserDropdown] Auto-verification result:', result);
  //           }
  //         }
  //       } catch (err) {
  //         console.error('[UserDropdown] Error during auto-verification:', err);
  //       }
  //     }
  //   };
    
  //   autoVerify();
    
  //   return () => {
  //     mounted = false;
  //   };
  // }, [user?.id, user?.email, verifiedStatus, isSuperadmin, clearCacheAndRecheck]);

  // useEffect(() => {
  //   if (user?.id) {
  //     console.log('[UserDropdown] Current user:', {
  //       id: user.id,
  //       email: user.email
  //     });
      
  //     console.log('[UserDropdown] Superadmin status:', { 
  //       isSuperadmin, 
  //       isSuperadminLoading,
  //       userId: user.id
  //     });
      
  //     if (!isSuperadminLoading && !isSuperadmin && !verifiedStatus) {
  //       const timer = setTimeout(() => {
  //         recheckSuperadminStatus().then(result => {
  //           console.log('[UserDropdown] Rechecked superadmin status:', { result });
  //           setVerifiedStatus(true);
  //         });
  //       }, 1000);
        
  //       return () => clearTimeout(timer);
  //     }
  //   }
  // }, [user?.id, isSuperadmin, isSuperadminLoading, recheckSuperadminStatus, verifiedStatus]);
  
  const isMobile = useIsMobile();
  
  const safeAvatarUrl = avatarUrl && typeof avatarUrl === 'string' && avatarUrl.trim() !== '' 
    ? avatarUrl 
    : null;
    
  // const handleClearRoleCache = async () => {
  //   try {
  //     toast({
  //       title: "Clearing role cache...",
  //       description: "Refreshing your superadmin status."
  //     });
      
  //     const result = await clearCacheAndRecheck();
      
  //     if (result) {
  //       toast({
  //         title: "Success!",
  //         description: "Superadmin access verified. Try clicking the menu item now.",
  //       });
  //     } else {
  //       toast({
  //         title: "Still no access",
  //         description: "Try logging out and back in to refresh your session.",
  //         variant: "destructive",
  //       });
  //     }
  //   } catch (error) {
  //     console.error("[UserDropdown] Error clearing cache:", error);
  //     toast({
  //       title: "Error",
  //       description: "Failed to clear cache. Try logging out instead.",
  //       variant: "destructive",
  //     });
  //   }
  // };
  
  const handleTripleClick = () => {
    setShowDebugMenu(true);
  };
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div 
          role="button" 
          className="cursor-pointer" 
          onMouseDown={(e) => {
            if (e.detail === 3) {
              handleTripleClick();
            }
          }}
        >
          <DropdownTrigger 
            avatarUrl={safeAvatarUrl}
            username={username}
            email={user?.email || null}
            getInitials={getInitials}
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className={`${isMobile ? 'w-[calc(100vw-32px)] max-w-[280px]' : 'w-64'} p-2 rounded-md shadow-lg bg-white border border-gray-200 z-50`}
        sideOffset={isMobile ? 4 : 8}
      >
        <DropdownHeader displayName={displayName} username={username} email={user?.email || null} />

        <DropdownMenuSeparator className="my-2 bg-gray-200/80" />
        
        <NavigationItems 
          userId={user?.id}
          isAdmin={isAdmin}
          isSuperadmin={isSuperadmin}
          isOnHomePage={isOnHomePage}
          isOnDashboard={isOnDashboard}
          isOnProfilePage={isOnProfilePage}
          isOnNotificationsPage={isOnNotificationsPage}
          isOnAdminPage={isOnAdminPage}
          isOnSettingsPage={isOnSettingsPage}
          isOnSuperadminPage={isOnSuperadminPage}
          username_slug={usernameSlug}
        />
        
        <DropdownMenuSeparator className="my-2 bg-gray-200/80" />
        
        <LogoutButton onLogout={onLogout} isLoggingOut={isLoggingOut} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserDropdown;
